import React from 'react';

interface Role {
    id: string;
    label: string;
    description: string;
    bg: string;
    text: string;
}

interface RoleSelectDialogProps {
    isOpen: boolean;
    currentRole: string;
    roles: Role[];
    userName: string;
    onSelect: (roleId: string) => void;
    onCancel: () => void;
}

const RoleSelectDialog: React.FC<RoleSelectDialogProps> = ({
    isOpen,
    currentRole,
    roles,
    userName,
    onSelect,
    onCancel
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-0 sm:p-4 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[85vh] overflow-hidden border-0 sm:border border-gray-100 dark:border-gray-700 animate-scaleIn flex flex-col">
                {/* Header - Fixed */}
                <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700 shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                                Chọn Vai Trò
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                                Chọn vai trò mới cho {userName}
                            </p>
                        </div>
                        {/* Close button on mobile */}
                        <button
                            onClick={onCancel}
                            className="sm:hidden ml-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all shrink-0"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    <div className="grid grid-cols-1 gap-3">
                        {roles.map((role) => (
                            <button
                                key={role.id}
                                onClick={() => onSelect(role.id)}
                                className={`text-left p-4 sm:p-5 rounded-xl border-2 transition-all active:scale-[0.98] ${currentRole === role.id
                                        ? `${role.bg} ${role.text} border-blue-500 ring-2 ring-blue-500/20`
                                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md'
                                    }`}
                            >
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <span className="font-bold text-sm sm:text-base">{role.label}</span>
                                    {currentRole === role.id && (
                                        <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full font-bold whitespace-nowrap">
                                            HIỆN TẠI
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    {role.description}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Footer - Fixed (Desktop only) */}
                <div className="hidden sm:flex p-6 border-t border-gray-100 dark:border-gray-700 justify-end gap-3 shrink-0">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2.5 rounded-xl font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        Đóng
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RoleSelectDialog;
